export type ProfanityLevel = 0 | 1 | 2;

/**
 * Two looks, one variant each (the user cut the rest on 2026-09-25):
 *   "sticker"   Pink Sticker: the pixel-matched port of the approved Paper
 *               artefact in hot pink (views/countdownSticker.ts)
 *   "chrome"    Neumorphic in its purple Chrome colourway: glossy Y2K
 *               chrome, blobs and soft light (views/countdownChrome.ts)
 * `theme`, `paperSkin` and `chromeVariant` keep their old names so saved
 * state still loads; anyone on a removed look lands on Pink Sticker.
 */
export type Theme = "sticker" | "chrome";

export const THEME_IDS: Theme[] = ["sticker", "chrome"];

/** Sticker's colour mode: only Pink is left. */
export type PaperSkin = "pink";

/** Neumorphic's colourway: only the purple Chrome one is left. */
export type ChromeVariant = "chrome";

/** Mirrors the --bg token each look resolves to in the stylesheets, so the <meta name="theme-color"> tag (status bar colour) can follow the live look. */
const BG = { pink: "#ff5ca8", chrome: "#e9d6fb" } as const;

export interface ResolvedLook {
  /** html[data-theme]: which stylesheet rules apply. Sticker's Settings, Edit
   * and onboarding use the paper rules in style.css, whose hook is still
   * called "neo-brutal" (the style it came from). */
  cssTheme: "neo-brutal" | "chrome";
  /** html[data-layout]: which countdown view renders. */
  layout: Theme;
  skin: PaperSkin;
  variant: ChromeVariant;
  dark: boolean;
  bg: string;
}

export function resolveLook(state: Pick<AppState, "theme" | "paperSkin" | "chromeVariant">): ResolvedLook {
  const base = { layout: state.theme, skin: "pink" as const, variant: "chrome" as const, dark: false };
  if (state.theme === "chrome") return { ...base, cssTheme: "chrome", bg: BG.chrome };
  return { ...base, cssTheme: "neo-brutal", bg: BG.pink };
}

export interface CountdownEvent {
  id: string;
  /** e.g. "Stag Do" */
  name: string;
  /** e.g. "Dan" — who it's for / who's dragging you into it */
  personName: string;
  /** ISO 8601, the moment being counted down to */
  targetDate: string;
  /** ISO 8601, when the countdown was created — used for the progress ruler */
  startDate: string;
}

export interface NotificationPrefs {
  enabled: boolean;
  /** mirrors Notification.permission so the UI doesn't have to ask again needlessly */
  lastPermission: NotificationPermission | "unsupported";
  /** local-date key (YYYY-MM-DD) the daily nudge last fired, so reopening the app doesn't repeat it */
  lastNotifiedDayKey?: string;
  /** set once the "it's here" notification has fired, so it never repeats */
  reachedNotified?: boolean;
  /** This device is subscribed to real push from the server, so reminders arrive with the app closed. */
  push?: boolean;
  /** Local hour (0 to 23) the daily push arrives. Default 9. */
  hour?: number;
  /** Show days left as the number on the Home Screen icon. Default on. */
  badge?: boolean;
}

export interface AppState {
  /** Array so a future version can hold more than one countdown without a data migration. */
  events: CountdownEvent[];
  /** Index into events of the one currently shown. Only 0 is used today. */
  activeEventId: string | null;
  theme: Theme;
  paperSkin: PaperSkin;
  chromeVariant: ChromeVariant;
  profanityLevel: ProfanityLevel;
  notifications: NotificationPrefs;
  /** Masks the hardest swear words in the Spicy/F**ked copy, in-app and in notifications. */
  censorEnabled: boolean;
}

const STORAGE_KEY = "cuntdown:v1";

function defaultState(): AppState {
  return {
    events: [],
    activeEventId: null,
    theme: "sticker",
    paperSkin: "pink",
    chromeVariant: "chrome",
    profanityLevel: 1,
    notifications: { enabled: false, lastPermission: "default" },
    censorEnabled: false,
  };
}

/** Highest valid ProfanityLevel — bump this alongside the type when adding/removing tiers. */
const MAX_PROFANITY_LEVEL = 2;

function load(): AppState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw);
    const merged: AppState = { ...defaultState(), ...parsed };
    // Guards against a stale value from a previous version with more/fewer
    // tiers (e.g. old "Unhinged" was index 3) — without this an out-of-range
    // level crashes PROFANITY_STEPS[level] lookups on render.
    if (
      typeof merged.profanityLevel !== "number" ||
      merged.profanityLevel < 0 ||
      merged.profanityLevel > MAX_PROFANITY_LEVEL
    ) {
      merged.profanityLevel = defaultState().profanityLevel;
    }
    // Only Pink Sticker and purple Neumorphic are left. Anyone on a removed
    // look (Dense Sky, Neobrutalist, Paper, Yellow, Aurora, Sunset) lands
    // on Pink Sticker.
    const legacyVariant = (merged as unknown as Record<string, unknown>).chromeVariant;
    if (merged.theme === "chrome" && legacyVariant !== undefined && legacyVariant !== "chrome") merged.theme = "sticker";
    delete (merged as unknown as Record<string, unknown>).neoCut;
    // The "Ideas for later" notes left Settings; phrases are edited in src/copy/.
    delete (merged as unknown as Record<string, unknown>).contentIdeas;
    if (!THEME_IDS.includes(merged.theme)) {
      merged.theme = defaultState().theme;
    }
    merged.paperSkin = "pink";
    merged.chromeVariant = "chrome";
    return merged;
  } catch {
    return defaultState();
  }
}

function persist(state: AppState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage unavailable (private mode, quota) — app still works for the session
  }
}

type Listener = (state: AppState) => void;

class Store {
  private state: AppState = load();
  private listeners = new Set<Listener>();

  get(): AppState {
    return this.state;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  update(mutate: (draft: AppState) => void) {
    const draft = structuredClone(this.state);
    mutate(draft);
    this.state = draft;
    persist(this.state);
    for (const fn of this.listeners) fn(this.state);
  }

  getPrimaryEvent(): CountdownEvent | null {
    const { events, activeEventId } = this.state;
    return events.find((e) => e.id === activeEventId) ?? events[0] ?? null;
  }

  /** `input.id` lets a caller pick the new event's id up front (onboarding does, so its preview copy matches day one). */
  saveEvent(input: { id?: string; name: string; personName: string; targetDate: string; startDate?: string }, existingId?: string) {
    this.update((draft) => {
      const now = new Date().toISOString();
      const existing = existingId ? draft.events.find((e) => e.id === existingId) : draft.events[0];
      if (existing) {
        existing.name = input.name;
        existing.personName = input.personName;
        existing.targetDate = input.targetDate;
        existing.startDate = input.startDate ?? existing.startDate;
      } else {
        const event: CountdownEvent = {
          id: input.id ?? crypto.randomUUID(),
          name: input.name,
          personName: input.personName,
          targetDate: input.targetDate,
          startDate: input.startDate ?? now,
        };
        draft.events.push(event);
        draft.activeEventId = event.id;
      }
    });
  }

  deleteEvent(id: string) {
    this.update((draft) => {
      draft.events = draft.events.filter((e) => e.id !== id);
      if (draft.activeEventId === id) {
        draft.activeEventId = draft.events[0]?.id ?? null;
      }
    });
  }

  setTheme(theme: Theme) {
    this.update((draft) => {
      draft.theme = theme;
    });
  }

  /** Changes any part of the look in one update, so a style and its variant land together. */
  setLook(look: Partial<Pick<AppState, "theme" | "paperSkin" | "chromeVariant">>) {
    this.update((draft) => {
      Object.assign(draft, look);
    });
  }

  setPaperSkin(skin: PaperSkin) {
    this.update((draft) => {
      draft.paperSkin = skin;
    });
  }

  setProfanityLevel(level: ProfanityLevel) {
    this.update((draft) => {
      draft.profanityLevel = level;
    });
  }

  setNotificationPrefs(prefs: Partial<NotificationPrefs>) {
    this.update((draft) => {
      draft.notifications = { ...draft.notifications, ...prefs };
    });
  }

  setCensorEnabled(enabled: boolean) {
    this.update((draft) => {
      draft.censorEnabled = enabled;
    });
  }

  resetAll() {
    localStorage.removeItem(STORAGE_KEY);
    this.state = defaultState();
    for (const fn of this.listeners) fn(this.state);
  }
}

export const store = new Store();
